import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  AUTH_SESSION_REDIRECT_PENDING_FLAG,
  DEFAULT_TENANT_ID,
  LOGOUT_IN_PROGRESS_FLAG,
} from '@/config/constants';
import { getSafePostLoginRedirect } from '@/lib/authRedirect';
import { authHelper } from '@/lib/localStore';
import { useUser } from '@/hooks/queries/auth';
import { ENV } from '@/config/env';
import type { User } from '@/types';

type CallbackResponse = {
  access_token?: string;
  refresh_token?: string;
  id_token?: string;
  accessToken?: string;
  refreshToken?: string;
  idToken?: string;
  token_type?: string;
  expires_in?: number;
};

const AUTH_ID_TOKEN_STORAGE_KEY = 'auth_id_token';

type ExistsResponse = {
  data?: boolean;
};

export const Route = createFileRoute('/callback')({
  component: CallbackPage,
});

function CallbackPage() {
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState('');
  const handledRef = useRef(false);
  const redirectUri = ENV.AUTHENTIK.REDIRECT_URI;
  const authBackendUrl = ENV.API_URL;

  useEffect(() => {
    if (handledRef.current) return;
    handledRef.current = true;
    const run = async () => {
      if (!redirectUri || !authBackendUrl) {
        setErrorMessage('Missing required authentication configuration');
        return;
      }

      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      const state = params.get('state');
      const oidcError = params.get('error');
      const oidcErrorDescription = params.get('error_description');
      const savedState = sessionStorage.getItem('auth_state');
      const codeVerifier = sessionStorage.getItem('auth_code_verifier') || '';
      const storedRedirectTo = sessionStorage.getItem('auth_redirect_to') || '/';
      const redirectTo = getSafePostLoginRedirect(storedRedirectTo);

      if (!code || !state) {
        if (oidcError) {
          const safeMessage = (oidcErrorDescription ?? oidcError).slice(0, 240);
          toast.error(safeMessage || 'Sign-in was canceled or failed.');
        }
        sessionStorage.removeItem(LOGOUT_IN_PROGRESS_FLAG);
        sessionStorage.removeItem(AUTH_SESSION_REDIRECT_PENDING_FLAG);
        navigate({
          to: '/authentikLogin',
          replace: true,
          search: {
            redirectTo: '/',
          },
        });
        return;
      }

      if (savedState && savedState !== state) {
        sessionStorage.removeItem(AUTH_SESSION_REDIRECT_PENDING_FLAG);
        setErrorMessage('Invalid state. Please try signing in again.');
        return;
      }

      try {
        const response = await fetch(`${authBackendUrl}/api/v1/auth/token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Tenant-Id': DEFAULT_TENANT_ID,
          },
          body: JSON.stringify({
            authCode: code,
            redirectUrl: redirectUri,
            codeVerifier: codeVerifier,
          }),
        });

        if (!response.ok) {
          let errorBody = '';
          try {
            errorBody = await response.text();
          } catch {
            errorBody = '';
          }
          void errorBody;
          throw new Error('Token exchange failed');
        }

        const payload = (await response.json()) as CallbackResponse;
        const token = payload.access_token || payload.accessToken;
        const refreshToken = payload.refresh_token || payload.refreshToken;
        const idToken = payload.id_token || payload.idToken;
        const user = getUserFromIdToken(idToken);

        if (!token) {
          throw new Error('No access token returned');
        }


        authHelper.token = token;
        useUser.getState().setToken(token);

        if (refreshToken) {
          authHelper.refreshToken = refreshToken;
          useUser.getState().setRefreshToken(refreshToken);
        }
        if (idToken) {
          sessionStorage.setItem(AUTH_ID_TOKEN_STORAGE_KEY, idToken);
        }

        if (user) {
          useUser.getState().setUser(user);
        }

        try {
          const userExistsEndpoint = `${authBackendUrl}/api/v1/users/exists`;
          const existsResponse = await fetch(userExistsEndpoint, {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${token}`,
              'X-Tenant-Id': DEFAULT_TENANT_ID,
            },
          });
          const existsBody = (await existsResponse.json().catch(() => null)) as ExistsResponse | null;
          const userExists = existsResponse.ok && existsBody?.data === true;
          if (!userExists) {
            const registerEndpoint = `${authBackendUrl}/api/v1/users/register`;
            const registerResponse = await fetch(registerEndpoint, {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${token}`,
                'X-Tenant-Id': DEFAULT_TENANT_ID,
              },
            });
            await registerResponse.text();
          }
        } catch {
          // Non-blocking: proceed with login even if user sync endpoints fail.
        }

        try {
          const currentUserEndpoint = `${authBackendUrl}/api/v1/users`;
          const currentUserResponse = await fetch(currentUserEndpoint, {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${token}`,
              'X-Tenant-Id': DEFAULT_TENANT_ID,
            },
          });
          const currentUserBody = (await currentUserResponse.json().catch(() => null)) as User | null;
          if (currentUserResponse.ok && currentUserBody) {
            useUser.getState().setUser(currentUserBody);
          }
        } catch {
          // Non-blocking: proceed with login even if current user fetch fails.
        }

        sessionStorage.removeItem('auth_state');
        sessionStorage.removeItem('auth_code_verifier');
        sessionStorage.removeItem('auth_redirect_to');
        sessionStorage.removeItem(LOGOUT_IN_PROGRESS_FLAG);
        sessionStorage.removeItem(AUTH_SESSION_REDIRECT_PENDING_FLAG);
        navigate({ to: redirectTo, replace: true });
      } catch (error) {
        // Avoid leaving stale logout state in this tab if callback fails.
        sessionStorage.removeItem(LOGOUT_IN_PROGRESS_FLAG);
        sessionStorage.removeItem(AUTH_SESSION_REDIRECT_PENDING_FLAG);
        setErrorMessage(error instanceof Error ? error.message : 'Authentication failed');
      }
    };

    run();
  }, [navigate, redirectUri, authBackendUrl]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="w-full max-w-md rounded-lg border bg-white/95 p-6 shadow-sm">
        <h2 className="text-center text-xl font-semibold">Completing sign in</h2>
        {errorMessage ? (
          <p className="text-destructive mt-3 text-center text-sm">{errorMessage}</p>
        ) : (
          <p className="text-muted-foreground mt-3 text-center text-sm">
            Please wait while we verify your login.
          </p>
        )}
      </div>
    </div>
  );
}

export default CallbackPage;

function getUserFromIdToken(idToken?: string): User | null {
  if (!idToken) return null;
  try {
    const payloadPart = idToken.split('.')[1];
    if (!payloadPart) return null;
    const base64 = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((char) => `%${(`00${char.charCodeAt(0).toString(16)}`).slice(-2)}`)
        .join('')
    );
    const claims = JSON.parse(json) as {
      sub?: string;
      preferred_username?: string;
      email?: string;
      name?: string;
      given_name?: string;
      family_name?: string;
    };

    const firstName = claims.given_name || claims.name?.split(' ')[0] || 'User';
    const lastName = claims.family_name || claims.name?.split(' ').slice(1).join(' ') || '';

    return {
      id: String(claims.sub || ''),
      username: claims.preferred_username || claims.email || firstName.toLowerCase(),
      firstName,
      lastName,
      email: claims.email || '',
      mobileNo: '',
      isEnabled: true,
      isBlocked: false,
      role: {
        id: '0',
        name: 'User',
      },
      organization: {
        id: '0',
        name: '',
      },
    };
  } catch {
    return null;
  }
}
